import { SemanticUI } from '@iterable/deprecated_react_components';
import { debounce } from 'lodash';
import React, {
  CSSProperties,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FixedSizeList } from 'react-window';
import { StyledDropdown, StyledInnerDropdownMenu } from './VirtualizedDropdown.styled';
import {
  DEAFULT_PROPS,
  DEFAULT_SELECTED_OPTION,
  DROPDOWN_ITEM_HEIGHT,
  DROPDOWN_MENU_HEIGHT,
  OVERSCAN_COUNT,
} from './constants';

type OuterMenuProps = {
  children: React.ReactNode;
  style: CSSProperties;
};

type InnerMenuProps = {
  children: React.ReactNode;
  style: CSSProperties;
};

export type VirtualizedDropdownProps = SemanticUI.DropdownProps & {
  selectedOption: SemanticUI.DropdownItemProps;
  options: SemanticUI.DropdownItemProps;
  onChange: (e: SyntheticEvent<HTMLElement, Event>, data: SemanticUI.DropdownItemProps) => void;
};

const OuterComponent = ({
  style: { position, overflow, ...restStyle },
  children,
  ...rest
}: OuterMenuProps) => (
  <SemanticUI.Dropdown.Menu className="outer" style={{ ...restStyle }} {...rest}>
    {children}
  </SemanticUI.Dropdown.Menu>
);

const InnerComponent = ({ children, style, ...rest }: InnerMenuProps) => {
  return (
    <StyledInnerDropdownMenu {...rest} open style={{ ...style }}>
      {children}
    </StyledInnerDropdownMenu>
  );
};

export const VirtualizedDropdown = (props: VirtualizedDropdownProps) => {
  const {
    onChange,
    options,
    className,
    selectedIndex: initialSelectedIndex,
    dataTest,
    isError,
    disabled,
  } = props;
  const [open, setOpen] = useState(false);
  const [filteredList, setFilteredList] = useState<SemanticUI.DropdownItemProps[]>(options);
  const [selectedIndex, setSelectedIndex] = useState<number>(initialSelectedIndex);
  const [selectedOption, setSelectedOption] = useState<SemanticUI.DropdownItemProps>(
    options?.[initialSelectedIndex] || DEFAULT_SELECTED_OPTION,
  );

  const ref = useRef<any>(null);
  const dropdownRef = useRef<any>(null);

  const tryScrollingToSelectedItem = (_open: boolean) => {
    if (ref.current && selectedIndex && _open) {
      ref.current?.scrollToItem(selectedIndex, 'center');
    }
  };

  useEffect(() => {
    tryScrollingToSelectedItem(open);
  }, [selectedIndex]);

  const onSearch = useCallback(
    (e: MouseEvent<HTMLDivElement>, _searchQuery: string) => {
      if (!_searchQuery) return options;

      const _filteredList = options.filter((option) => {
        if (typeof option.text === 'string') {
          return option?.text?.toLowerCase().includes(_searchQuery.toLowerCase());
        }

        return false;
      });

      return _filteredList;
    },
    [options],
  );

  const onSearchChange = useCallback(
    (e: MouseEvent<HTMLDivElement>, { searchQuery: _searchQuery }: any) => {
      const _filteredList = onSearch(e, _searchQuery);
      setFilteredList(_filteredList);
    },
    [onSearch],
  );

  const onClose = useCallback(() => {
    setFilteredList(options);
    setOpen(false);
  }, [options]);

  const onOpen = () => {
    setOpen(true);
    tryScrollingToSelectedItem(true);
  };

  const onItemClick = useCallback(
    (e: MouseEvent<HTMLDivElement>, option: SemanticUI.DropdownItemProps) => {
      setSelectedOption(option);
      setSelectedIndex(option?.index);
      onChange(e, option);
      setOpen(false);

      dropdownRef?.current?.handleItemClick?.(e, option);
    },
    [],
  );

  const renderDropdownItem = useCallback(
    ({ index, style, data }: any) => {
      const noResultsOption = { disabled: true, value: '', text: 'No results found.' };

      const { options: _options, selectedOption: _selectedOption } = data;
      const option = _options?.[index] || noResultsOption;
      const key = `dropdown-item-${index}`;

      return (
        <SemanticUI.Dropdown.Item
          {...option}
          key={key}
          style={style}
          onClick={(e: MouseEvent<HTMLDivElement>, clickedOption: SemanticUI.DropdownItemProps) =>
            onItemClick(e, { ...clickedOption, index })
          }
          name={option?.text}
          disabled={disabled}
          active={option.value === _selectedOption?.value}
          selected={option.value === _selectedOption?.value}
          className="item"
        />
      );
    },
    [onItemClick, disabled],
  );

  const itemCount = filteredList?.length === 0 ? 1 : filteredList.length;

  const height =
    itemCount * DROPDOWN_ITEM_HEIGHT < DROPDOWN_MENU_HEIGHT
      ? itemCount * DROPDOWN_ITEM_HEIGHT + 1
      : DROPDOWN_MENU_HEIGHT;

  return (
    <StyledDropdown
      {...DEAFULT_PROPS}
      {...props}
      error={isError}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      search={true}
      onSearchChange={debounce(onSearchChange, 300)}
      className={`selection ${className}`}
      ref={dropdownRef}
      data-test={dataTest}
      disabled={disabled}
    >
      <FixedSizeList
        width={'100%'}
        innerElementType={InnerComponent}
        outerElementType={OuterComponent}
        itemCount={itemCount}
        itemSize={DROPDOWN_ITEM_HEIGHT}
        overscanCount={OVERSCAN_COUNT}
        itemData={{ options: filteredList, selectedOption }}
        height={height}
        ref={ref}
      >
        {renderDropdownItem}
      </FixedSizeList>
    </StyledDropdown>
  );
};
